import { mermaid } from '@streamdown/mermaid';
import React, { useEffect, useState } from 'react';
import rehypeSanitize from 'rehype-sanitize';
import { defaultRehypePlugins, defaultRemarkPlugins, Streamdown } from 'streamdown';
import { TableFileRequestProvider } from '../../aiChatTableDownloadContext';
import type { TableFileFormat } from '../../utils/tableExport';
import { unwrapUnsupportedAiChatLinks } from './aiChatLinkResolver';
import { aiChatSanitizeSchema } from './aiChatSanitizeSchema';
import { keepTableTokensWhole } from './keepTableTokensWhole';
import { useAiChatMarkdownComponents } from './markdownComponents';
import { repairAiChatLinkLabels } from './repairAiChatLinkLabels';
import { code } from './shikiCodePlugin';

interface MessageTextPartProps {
    text: string;
    /** The format of the file the user asked for, when this text holds the table that answers the request. */
    fileFormat?: TableFileFormat;
    /** Whether this text is still arriving: its new words resolve in turn, and the caret sits at its end. */
    streaming?: boolean;
}

const aiChatPlugins = { code, mermaid };

// Streamdown's own word split, drawn by `markdownComponents` as the streaming-text recipe's words.
const STREAMED_WORDS = { sep: 'word' } as const;

// The caret at the end of text still arriving: Streamdown's own caret classes sit in node_modules, which Tailwind
// does not scan. It holds still, as nothing decorative loops.
const CARET_CLASS_NAME =
    '*:last:after:ms-0.5 *:last:after:inline-block *:last:after:h-[1.05em] *:last:after:w-0.5 *:last:after:rounded-[1px] *:last:after:bg-foreground *:last:after:align-[-0.15em]';

// A backlog of arrived words is caught up over this many gaps, so text that streams faster than a word a gap
// never falls far behind it.
const CATCH_UP_GAPS = 8;

/**
 * Streaming text: the words under `root` resolve in order, one every `--stream-gap`, and several at a time when the
 * text arrives faster than that. A word re-rendered away and back is shown again on the next gap.
 */
const useStreamedWordsReveal = (root: HTMLElement | null, streaming: boolean) => {
    useEffect(() => {
        if (!root || !streaming) {
            return undefined;
        }
        const gap = parseFloat(getComputedStyle(root).getPropertyValue('--stream-gap')) || 60;
        let shown = 0;
        const timer = setInterval(() => {
            const words = root.querySelectorAll('.t-stream-w');
            shown = Math.min(words.length, shown + Math.max(1, Math.ceil((words.length - shown) / CATCH_UP_GAPS)));
            words.forEach((word, index) => index < shown && word.classList.add('is-in'));
        }, gap);
        return () => clearInterval(timer);
    }, [root, streaming]);
};

// The prop replaces Streamdown's own remark plugins (GFM), so they are listed again in front.
const aiChatRemarkPlugins = [...Object.values(defaultRemarkPlugins), repairAiChatLinkLabels];

/*
 * The code styling the markdown surface lays over `.markdown-prose`. Descendant rules, because the code
 * plugin renders its own `pre` and `code`. The plugin also styles each line with Tailwind's `block`, but
 * its classes live in node_modules, which Tailwind does not scan, so the line layout is restated here.
 * Classic's monospace stack is written out, twice (the new design takes `font-mono`), because the `font-mono`
 * token leads with Roboto Mono and Tailwind only generates a class it finds spelled out in full. For the same
 * reason Streamdown's own `*:first:mt-0 *:last:mb-0` on its block list is never generated, so it is restated: only
 * what holds the text sets the space above the first block and below the last, a question's bubble padding, which
 * centres a one-line question, or the gap between an answer's parts. In the new design prose stops at 60ch, while a
 * table or a chart keeps the answer's full width.
 */
const MARKDOWN_CLASS_NAME = [
    'markdown-prose',
    '[&>*>:first-child]:mt-0 [&>*>:last-child]:mb-0',
    '[&_pre]:border-fg/12 [&_pre]:bg-fg/4 [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-[6px] [&_pre]:border [&_pre]:px-4 [&_pre]:py-3',
    '[&_pre_code]:block [&_pre_code]:text-[0.85rem] [&_pre_code]:leading-[1.6] [&_pre_code]:whitespace-pre',
    '[&_pre_code]:font-[family-name:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation_Mono,Courier_New,monospace] new:[&_pre_code]:font-mono',
    '[&_[data-streamdown=code-block-body]_code>span]:block',
    '[&_:not(pre)>code]:border-fg/10 [&_:not(pre)>code]:bg-fg/6 [&_:not(pre)>code]:rounded-[4px] [&_:not(pre)>code]:border [&_:not(pre)>code]:px-1.5 [&_:not(pre)>code]:py-0.5 [&_:not(pre)>code]:text-[0.85em]',
    '[&_:not(pre)>code]:font-[family-name:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation_Mono,Courier_New,monospace] new:[&_:not(pre)>code]:font-mono',
    'new:[&_:is(p,li,blockquote)]:max-w-[60ch]',
].join(' ');

const MessageTextPart: React.FC<MessageTextPartProps> = ({ text, fileFormat, streaming = false }) => {
    const components = useAiChatMarkdownComponents();
    const [cardSlot, setCardSlot] = useState<HTMLDivElement | null>(null);
    const [root, setRoot] = useState<HTMLDivElement | null>(null);
    useStreamedWordsReveal(root, streaming);

    if (!text) {
        return null;
    }

    return (
        <div ref={setRoot} className={MARKDOWN_CLASS_NAME}>
            {/* Where the requested file's card goes, above the heading of its table, which draws it. The card's
                container query reads this full-width slot, the answer's width: on a box its content sizes, it
                would collapse that box. The slot holds the gaps around the card: MARKDOWN_CLASS_NAME takes the
                margins off the first and last child of each text block's children, the card included. */}
            {fileFormat ? (
                <div ref={setCardSlot} className="@container w-full not-empty:mb-4 [:not(:first-child)>&]:mt-3" />
            ) : null}
            <TableFileRequestProvider value={fileFormat && cardSlot ? { format: fileFormat, slot: cardSlot } : null}>
                <Streamdown
                    mode="streaming"
                    isAnimating={streaming}
                    animated={STREAMED_WORDS}
                    className={streaming ? CARET_CLASS_NAME : undefined}
                    controls={false}
                    skipHtml={true}
                    components={components}
                    remarkPlugins={aiChatRemarkPlugins}
                    rehypePlugins={[
                        unwrapUnsupportedAiChatLinks,
                        [rehypeSanitize, aiChatSanitizeSchema],
                        defaultRehypePlugins.harden,
                        keepTableTokensWhole,
                    ]}
                    plugins={aiChatPlugins}
                >
                    {text}
                </Streamdown>
            </TableFileRequestProvider>
        </div>
    );
};

export default React.memo(MessageTextPart);
